import { useCallback, useEffect, useState } from 'react'
import { FiCheck, FiTrash2, FiLoader, FiAlertTriangle, FiCopy } from 'react-icons/fi'
import { GiBrain } from 'react-icons/gi'
import SidePanel from './SidePanel'

// ═══ REVISÃO DO APRENDIZADO DO PAINEL DADOS ═══
//
// [2026-09-29] Pente-fino: ninguém votava nas respostas desde julho, então
// nada era promovido ao vault e o painel parou de aprender. Agora a própria
// conversa separa as candidatas (refinou = superada; contestou = suspeita;
// mudou de assunto ou parou = aceita) e o backend reescreve cada pergunta
// como completa. Aqui o admin só aprova ou descarta EM LOTE.
//
// Recebe authenticatedFetch por PROP (como o ReaberturaJanela) para dar pra
// montar numa banca isolada com o fetch dublado.

interface Candidata {
  id: number
  pergunta: string
  pergunta_original: string
  sql: string
  explicacao: string
  autor: string | null
  aceita_em: string | null
  travas: string[]
  parecida_com: string | null
}

interface Props {
  isOpen: boolean
  onClose: () => void
  authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>
  /** Avisa o painel de quantas candidatas sobraram (badge do botão). */
  onContagem?: (n: number) => void
}

// [singular, plural]
const ROTULO: Record<string, [string, string]> = {
  aceita: ['aceita', 'aceitas'], pendente: ['em conversa', 'em conversa'],
  refinada: ['refinada', 'refinadas'], contestada: ['contestada', 'contestadas'],
}

function quando(ts: string | null) {
  if (!ts) return ''
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T'))
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function RevisaoAprendizado({ isOpen, onClose, authenticatedFetch, onContagem }: Props) {
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [lista, setLista] = useState<Candidata[]>([])
  const [resumo, setResumo] = useState<Record<string, number>>({})
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set())
  const [enviando, setEnviando] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)
  // SQL aberto por cartão. Botão + estado em vez de <details>: as variantes
  // group-open:* eram classes inéditas e a landing paga o CSS de todo o CRM
  // (teto de 200 KB em scripts/check-bundle-landing.cjs).
  const [sqlAberto, setSqlAberto] = useState<Set<number>>(new Set())

  const carrega = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const r = await authenticatedFetch('/api/oracle/aprendizado/candidatas')
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json() as { resumo: { aceite: string; n: number }[]; candidatas: Candidata[] }
      const res: Record<string, number> = {}
      for (const x of d.resumo || []) if (x.aceite !== 'legado') res[x.aceite] = Number(x.n)
      setResumo(res)
      setLista(d.candidatas || [])
      setMarcadas(new Set())
      onContagem?.((d.candidatas || []).length)
    } catch (e) {
      setErro(`Não consegui carregar as candidatas (${(e as Error).message}).`)
    } finally {
      setCarregando(false)
    }
  }, [authenticatedFetch, onContagem])

  useEffect(() => { if (isOpen) { setRecado(null); carrega() } }, [isOpen, carrega])

  const alterna = (id: number) => setMarcadas(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n
  })
  const aprovaveis = lista.filter(c => c.travas.length === 0)
  const todasMarcadas = aprovaveis.length > 0 && aprovaveis.every(c => marcadas.has(c.id))

  const decide = async (acao: 'promover' | 'descartar') => {
    const ids = [...marcadas]
    if (!ids.length) return
    setEnviando(true); setRecado(null)
    try {
      const r = await authenticatedFetch('/api/oracle/aprendizado/decidir', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(acao === 'promover' ? { promover: ids } : { descartar: ids }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json() as { resultado: { id: number; ok: boolean; motivo?: string }[]; descartadas: number }
      if (acao === 'promover') {
        const ok = d.resultado.filter(x => x.ok).length
        const falhas = d.resultado.filter(x => !x.ok)
        setRecado(`${ok} ${ok === 1 ? 'consulta aprovada — já vale' : 'consultas aprovadas — já valem'} como exemplo nas próximas perguntas.`
          + (falhas.length ? ` ${falhas.length} não ${falhas.length === 1 ? 'entrou' : 'entraram'}: ${falhas.map(f => `#${f.id} (${f.motivo})`).join(', ')}.` : ''))
      } else {
        setRecado(`${d.descartadas} ${d.descartadas === 1 ? 'consulta descartada' : 'consultas descartadas'}.`)
      }
      await carrega()
    } catch (e) {
      setRecado(`Não deu certo (${(e as Error).message}). Nada foi alterado.`)
    } finally {
      setEnviando(false)
    }
  }

  const chips = ['aceita', 'pendente', 'refinada', 'contestada']
    .filter(k => resumo[k])
    .map(k => `${resumo[k]} ${ROTULO[k][resumo[k] === 1 ? 0 : 1]}`)

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      closeOnScrim={false}
      icon={<GiBrain className="w-5 h-5 text-violet-500" />}
      title="Revisar aprendizado"
      subtitle={chips.length ? chips.join(' · ') : 'Consultas que a conversa aceitou'}
      footer={!lista.length ? undefined : (
        <div className="px-4 py-3 flex items-center gap-2">
          <button
            onClick={() => setMarcadas(todasMarcadas ? new Set() : new Set(aprovaveis.map(c => c.id)))}
            disabled={!aprovaveis.length || enviando}
            className="h-9 px-3 rounded-lg text-[13px] text-muted-foreground hover:bg-secondary disabled:opacity-40">
            {todasMarcadas ? 'Desmarcar' : 'Marcar todas'}
          </button>
          <div className="flex-1" />
          <button
            onClick={() => decide('descartar')}
            disabled={!marcadas.size || enviando}
            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg text-[13px] text-rose-600 dark:text-rose-400 hover:bg-secondary disabled:opacity-40">
            <FiTrash2 className="w-4 h-4" /> Descartar
          </button>
          <button
            onClick={() => decide('promover')}
            disabled={!marcadas.size || enviando || [...marcadas].some(id => lista.find(c => c.id === id)?.travas.length)}
            className="h-9 px-4 inline-flex items-center gap-1.5 rounded-lg text-[13px] font-medium bg-primary text-primary-foreground disabled:opacity-40">
            {enviando ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />}
            Aprovar {marcadas.size ? `(${marcadas.size})` : ''}
          </button>
        </div>
      )}
    >
      <div className="p-4 flex flex-col gap-3">
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          Aprovar ensina o painel: a pergunta vira exemplo e, se alguém fizer a mesma pergunta de novo,
          o SQL aprovado é reaproveitado. Na dúvida, deixe na fila.
        </p>

        {recado && (
          <div className="text-[13px] px-3 py-2 rounded-lg bg-secondary text-foreground">{recado}</div>
        )}
        {erro && <div className="text-[13px] px-3 py-2 rounded-lg bg-rose-500/10 text-rose-700 dark:text-rose-300">{erro}</div>}

        {carregando && !lista.length && (
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground py-6 justify-center">
            <FiLoader className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        )}

        {!carregando && !erro && !lista.length && (
          <div className="text-[13px] text-muted-foreground py-8 text-center">
            Nenhuma candidata agora. Elas aparecem conforme as conversas do painel terminam.
          </div>
        )}

        {lista.map(c => {
          const bloqueada = c.travas.length > 0
          const marcada = marcadas.has(c.id)
          return (
            <div key={c.id}
              className={`rounded-xl border px-3 py-2.5 transition-colors ${marcada ? 'border-primary bg-primary/5' : 'border-border'}`}>
              <label className={`flex items-start gap-2.5 ${bloqueada ? 'cursor-default' : 'cursor-pointer'}`}>
                <input type="checkbox" className="mt-1 w-4 h-4 shrink-0"
                  checked={marcada} disabled={bloqueada}
                  onChange={() => alterna(c.id)} />
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] text-foreground leading-snug">{c.pergunta}</p>
                  {c.pergunta_original && c.pergunta_original.trim() !== c.pergunta.trim() && (
                    <p className="text-[12px] text-muted-foreground mt-0.5 break-words">
                      digitou: “{c.pergunta_original.slice(0, 140)}{c.pergunta_original.length > 140 ? '…' : ''}”
                    </p>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-1">
                    #{c.id}{c.autor ? ` · ${c.autor}` : ''}{c.aceita_em ? ` · ${quando(c.aceita_em)}` : ''}
                  </p>
                </div>
              </label>

              {bloqueada && (
                <div className="mt-2 flex items-start gap-1.5 text-[12px] text-rose-700 dark:text-rose-300">
                  <FiAlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Não pode ser aprovada: {c.travas[0]}</span>
                </div>
              )}
              {!bloqueada && c.parecida_com && (
                <div className="mt-2 flex items-start gap-1.5 text-[12px] text-amber-700 dark:text-amber-300">
                  <FiAlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Quase igual a um exemplo já aprovado — aprovar só soma, não cria outro.</span>
                </div>
              )}

              <button
                onClick={() => setSqlAberto(prev => { const n = new Set(prev); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n })}
                className="mt-2 text-[12px] text-primary">
                {sqlAberto.has(c.id) ? 'Esconder SQL' : 'Ver SQL'}
              </button>
              {sqlAberto.has(c.id) && (<>
                <div className="relative mt-1.5">
                  <pre className="text-[11px] leading-relaxed bg-secondary rounded-lg p-2.5 overflow-x-auto whitespace-pre-wrap break-words max-h-64 overflow-y-auto [scrollbar-width:none]">{c.sql}</pre>
                  <button onClick={() => navigator.clipboard?.writeText(c.sql)}
                    title="Copiar SQL" aria-label="Copiar SQL"
                    className="absolute top-1.5 right-1.5 w-7 h-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-secondary">
                    <FiCopy className="w-3.5 h-3.5" />
                  </button>
                </div>
                {c.explicacao && <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed">{c.explicacao}</p>}
              </>)}
            </div>
          )
        })}
      </div>
    </SidePanel>
  )
}
